import { AgGridReact, AgGrid } from "@jsq/ds/core";
import { MoneyCellRenderer } from "~/pages/Billing/components/MoneyCellRenderer";
import {
  BILLING_STATUS_FILTER_VALUES,
  ENTITY_STATUS_FILTER_VALUES,
  GRID_COLUMN_FILTER_DEBOUNCE_MS,
  PRODUCT_LINE_FILTER_VALUES,
} from "../../constants";
import { EntityCellRenderer } from "./EntityCellRenderer";

interface ColumnDefParams {
  isBillingEntityStatusUpdateFlagEnabled: boolean;
  isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled: boolean;
  isBillingEnableFADataFlagEnabled: boolean;
  isSafeCaseIdAndActiveInvestmentsEnabled: boolean;
  renderEntityStatusCell?: (params: AgGrid.ICellRendererParams) => React.ReactElement;
  isEntityStatusEditable?: (params: AgGrid.EditableCallbackParams) => boolean;
  onEntityStatusChange?: (params: AgGrid.NewValueParams) => Promise<void>;
}

export const createDefaultEntitiesColumnDefs = ({
  isBillingEntityStatusUpdateFlagEnabled,
  isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled,
  isBillingEnableFADataFlagEnabled,
  isSafeCaseIdAndActiveInvestmentsEnabled,
  renderEntityStatusCell,
  isEntityStatusEditable,
  onEntityStatusChange,
}: ColumnDefParams): AgGridReact.AgGridReactProps["columnDefs"] => [
  {
    field: "entity",
    headerName: "Entity",
    headerTooltip: "JSQ Entity Name",
    minWidth: 260,
    width: 260,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    pinned: "left",
    hide: false,
    ...(isBillingEntityStatusUpdateFlagEnabled && {
      cellRenderer: EntityCellRenderer,
    }),
  },
  ...(isBillingEnableFADataFlagEnabled
    ? [
        {
          field: "customerLegalName",
          headerName: "Customer Legal Name",
          headerTooltip: "Customer entity Legal Name",
          minWidth: 200,
          width: 200,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            maxNumConditions: 1,
            debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          },
          hide: true,
        },
      ]
    : []),
  {
    field: "entityId",
    headerName: "Entity ID",
    headerTooltip: "Global ID for clients across internal systems. Set by BizOps.",
    minWidth: 120,
    width: 120,
    filter: "agNumberColumnFilter",
    filterParams: {
      filterOptions: ["equals"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  ...(isBillingEnableFADataFlagEnabled
    ? [
        {
          field: "sfdcEntityId",
          headerName: "SFDC Entity ID",
          headerTooltip: "Salesforce Entity ID",
          minWidth: 150,
          width: 150,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            maxNumConditions: 1,
            debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          },
          hide: false,
        },
      ]
    : []),
  ...(isSafeCaseIdAndActiveInvestmentsEnabled
    ? [
        {
          field: "safeCaseId",
          headerName: "Safe Case ID",
          headerTooltip: "Salesforce Safe Case ID",
          minWidth: 150,
          width: 150,
          hide: false,
        },
      ]
    : []),
  {
    field: "faEntityNumber",
    headerName: "FA Entity Number",
    headerTooltip: "Fund Admin Entity Number",
    minWidth: 150,
    width: 150,
    cellStyle: { textAlign: "right" },
    hide: false,
  },
  {
    field: "jsqProductLine",
    headerName: "JSQ product line",
    headerTooltip: "JSQ Product Line",
    minWidth: 150,
    width: 150,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    hide: false,
    ...(isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled && {
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
        values: PRODUCT_LINE_FILTER_VALUES,
      },
    }),
  },
  {
    field: "billingStatus",
    headerName: "Billing status",
    headerTooltip: 'Entity billing status: "Billable" vs "Non-billable".',
    minWidth: 120,
    width: 120,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    hide: false,
    ...(isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled && {
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
        values: BILLING_STATUS_FILTER_VALUES,
      },
    }),
  },
  {
    field: "entityStatus",
    headerName: "Entity status",
    headerTooltip: 'Entity status: "Active" vs "Complete"',
    minWidth: 120,
    width: 120,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    hide: false,
    ...(isBillingEntityStatusUpdateFlagEnabled && {
      cellRenderer: renderEntityStatusCell,
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: ["Active", "Completed"],
        cellHeight: 40,
      },
      editable: isEntityStatusEditable,
      singleClickEdit: true,
      cellStyle: { alignContent: "center" },
      onCellValueChanged: onEntityStatusChange,
    }),
    ...(isBillingAgGridStaticClientDetailsSetFiltersFlagEnabled && {
      filter: "agSetColumnFilter",
      filterParams: {
        debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
        values: ENTITY_STATUS_FILTER_VALUES,
      },
    }),
  },
  ...(isBillingEnableFADataFlagEnabled
    ? [
        {
          field: "faEntityType",
          headerName: "FA Entity Type",
          headerTooltip: "Indicates the type of fund (Main Fund, Feeder, HoldCo, Master, etc)",
          minWidth: 150,
          width: 150,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            maxNumConditions: 1,
            debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          },
          hide: true,
        },
        {
          field: "faEumType",
          headerName: "FA EUM Type",
          headerTooltip: "EUM calculation type per FA contract. Drives FA EUM calculation.",
          minWidth: 150,
          width: 150,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            maxNumConditions: 1,
            debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          },
          hide: true,
        },
        {
          field: "faFundFamily",
          headerName: "FA Fund Family",
          headerTooltip: "Family of fund entities which are related together.",
          minWidth: 150,
          width: 150,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            maxNumConditions: 1,
            debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
          },
          hide: true,
        },
        {
          field: "serviceStartDate",
          headerName: "Service Start Date",
          headerTooltip: "Date that services and billing start.",
          minWidth: 150,
          width: 150,
          hide: true,
        },
        {
          field: "serviceEndDate",
          headerName: "Service End Date",
          headerTooltip: "Date that services and billing end.",
          minWidth: 150,
          width: 150,
          hide: true,
        },
        ...(isSafeCaseIdAndActiveInvestmentsEnabled
          ? [
              {
                field: "feeBasis",
                headerName: "Fee Basis",
                headerTooltip: "Fee basis used to calculate fees for the entity.",
                minWidth: 180,
                width: 200,
                sortable: true,
                filter: "agTextColumnFilter",
                filterParams: {
                  filterOptions: ["contains"],
                  maxNumConditions: 1,
                  debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
                },
                valueFormatter: (params: AgGrid.ValueFormatterParams) =>
                  params.value == null ? "--" : String(params.value),
                hide: false,
              },
              {
                field: "activeInvestments",
                headerName: "Active Investments",
                headerTooltip:
                  "Count of distinct active GL deals — per entity, with billing-group rows showing the total across entities in the group.",
                minWidth: 180,
                width: 180,
                filter: false,
                cellStyle: { textAlign: "right" },
                valueFormatter: (params: AgGrid.ValueFormatterParams) =>
                  params.value == null ? "--" : String(params.value),
                hide: false,
              },
            ]
          : []),
      ]
    : []),
  {
    field: "currency",
    headerName: "Currency",
    headerTooltip: "Currency setting for the entity",
    minWidth: 100,
    width: 100,
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
      debounceMs: GRID_COLUMN_FILTER_DEBOUNCE_MS,
    },
    hide: false,
  },
  {
    field: "fxRate",
    headerName: "FX Rate",
    headerTooltip: "Local currency to USD FX rate for the day",
    minWidth: 100,
    width: 100,
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "positionCountOfActiveInvestors",
    headerName: "Active Investors Count",
    headerTooltip:
      "Count of positions, excluding positions: with entity relationship, Closed positions",
    minWidth: 180,
    width: 180,
    cellStyle: { textAlign: "right" },
    hide: false,
  },
  {
    field: "commitmentsLocal",
    headerName: "Commitments (Local)",
    headerTooltip:
      "Aggregate of commitments in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "commitmentsUsd",
    headerName: "Commitments (USD)",
    headerTooltip: "Commitments FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "contributionsLocal",
    headerName: "Contributions (Local)",
    headerTooltip:
      "Aggregate of commitments in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "contributionsUsd",
    headerName: "Contributions (USD)",
    headerTooltip: "Contributions FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "redemptionsLocal",
    headerName: "Redemptions (Local)",
    headerTooltip:
      "Aggregate of redemptions in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "redemptionsUsd",
    headerName: "Redemptions (USD)",
    headerTooltip: "Redemptions FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "distributionsLocal",
    headerName: "Distributions (Local)",
    headerTooltip:
      "Aggregate of distributions in local currency for the entity (value seen in workspace)",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "distributionsUsd",
    headerName: "Distributions (USD)",
    headerTooltip: "Distributions FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "currentBlendSoftwareEumLocal",
    headerName: "Software EUM (Local)",
    headerTooltip:
      "Total Sofware EUM in the client's local currency, inclusive of: entity is marked as complete or test, Positions marked as complete, Positions with entity ownership",
    minWidth: 250,
    width: 250,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "currentBlendSoftwareEumUsd",
    headerName: "Software EUM (USD)",
    headerTooltip: "Total USD Software EUM (fx'd)",
    minWidth: 250,
    width: 250,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: false,
  },
  ...(isBillingEnableFADataFlagEnabled
    ? [
        {
          field: "billableFaEumLocal",
          headerName: "Billable FA EUM (Local)",
          headerTooltip:
            "EUM used for billing of FA services in local currency. Depends on the EUM type selected.",
          minWidth: 200,
          width: 200,
          cellRenderer: MoneyCellRenderer,
          cellRendererParams: {
            currencyField: "currency",
            rounded: false,
          },
          cellStyle: { textAlign: "right" },
          hide: true,
        },
        {
          field: "billableFaEumUsd",
          headerName: "Billable FA EUM (USD)",
          headerTooltip:
            "EUM used for billing of FA services in USD. Depends on the EUM type selected.",
          minWidth: 200,
          width: 200,
          cellRenderer: MoneyCellRenderer,
          cellRendererParams: {
            rounded: false,
          },
          cellStyle: { textAlign: "right" },
          hide: false,
        },
        {
          field: "flatFeeRate",
          headerName: "Flat Fee Rate",
          headerTooltip: "The flat fee",
          minWidth: 120,
          width: 120,
          cellStyle: { textAlign: "right" },
          hide: false,
        },
        {
          field: "countWirePayments",
          headerName: "Count of Wire payments",
          headerTooltip: "Count of wire payments for the period (month to date)",
          minWidth: 150,
          width: 150,
          cellStyle: { textAlign: "right" },
          hide: false,
        },
        {
          field: "countAchPayments",
          headerName: "Count of ACH payments",
          headerTooltip: "Count of ACH payments for the period (month to date)",
          minWidth: 150,
          width: 150,
          cellStyle: { textAlign: "right" },
          hide: false,
        },
        {
          field: "countCheckPayments",
          headerName: "Count of Check payments",
          headerTooltip: "Count of Check payments for the period (month to date)",
          minWidth: 150,
          width: 150,
          cellStyle: { textAlign: "right" },
          hide: false,
        },
      ]
    : []),
  {
    field: "returnOfCapitalLocal",
    headerName: "Return of Capital Distribution Amount (Local)",
    headerTooltip: "Return of Capital Distribution Amount",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      currencyField: "currency",
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
  {
    field: "returnOfCapitalUsd",
    headerName: "Return of Capital Distribution Amount (USD)",
    headerTooltip: "Return of Capital Distribution Amount FX'd to USD",
    minWidth: 170,
    width: 170,
    cellRenderer: MoneyCellRenderer,
    cellRendererParams: {
      rounded: false,
    },
    cellStyle: { textAlign: "right" },
    hide: true,
  },
];
